import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DataTable from "../../../components/DataTable";
import { orderService } from "../../../services/order/orderService";
import type { OrderDetailResponse } from "../../../types/api.types";
import { useSnackbar } from "../../../context/SnackbarContext";
import { COMPANY_CHANGED_EVENT } from "../../../utils/constants";
import { StockErrorDialog } from "../../../shared/StockErrorDialog";
import { number } from "motion";

const CartPage = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("orderId");

  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [items, setItems] = useState<OrderDetailResponse[]>([]);
  const [orderStatus, setOrderStatus] = useState<string | undefined>(undefined);
  const [companyKey, setCompanyKey] = useState(0);

  const [isExporting, setIsExporting] = useState(false);
  const [observations, setObservations] = useState("");

  const [stockErrors, setStockErrors] = useState<string[]>([]);

  const [orderNro, setOrderNro] = useState<number | null>(null);

  useEffect(() => {
    const onCompanyChanged = () => setCompanyKey((k) => k + 1);
    window.addEventListener(COMPANY_CHANGED_EVENT, onCompanyChanged);
    return () =>
      window.removeEventListener(COMPANY_CHANGED_EVENT, onCompanyChanged);
  }, []);

  useEffect(() => {
    if (!orderId) return;
    let isActive = true;

    (async () => {
      try {
        setIsLoading(true);
        setOrderStatus(undefined);
        const response = await orderService.getOrderDetails(orderId);
        if (!isActive) return;
        setItems(response.items);
        setOrderStatus(response.status);
        setObservations(response.observations ?? "");
        setOrderNro(response.consecutiveNumber ?? 0);
      } catch (error: any) {
        showSnackbar(
          error?.response?.data?.message || "Error al cargar el carrito",
          "error",
          4000,
        );
        setItems([]);
        setOrderStatus(undefined);
        setObservations("");
      } finally {
        if (isActive) setIsLoading(false);
      }
    })();

    return () => {
      isActive = false;
    };
  }, [orderId, companyKey, showSnackbar]);

  const subtotal = useMemo(() => {
    return items.reduce(
      (sum, it) => sum + (it.quantity || 0) * it.unitPrice,
      0,
    );
  }, [items]);

  const cartColumns = useMemo(
    () => [
      {
        header: "Producto",
        key: "product",
        render: (row: OrderDetailResponse) => (
          <Typography fontWeight={600}>{row.product}</Typography>
        ),
      },
      {
        header: "Tipo de producto",
        key: "productTypeId",
        render: (row: OrderDetailResponse) => (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={row.productTypeId}
          >
            {row.productTypeId?.trim?.() ?? row.productTypeId}
          </Typography>
        ),
      },
      {
        header: "Cantidad",
        key: "quantity",
        render: (row: OrderDetailResponse) => (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              textAlign: "center",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={row.quantity?.toString?.() ?? String(row.quantity)}
          >
            {row.quantity?.toString?.() ?? row.quantity}
          </Typography>
        ),
      },
      {
        header: "Unitario",
        key: "unitPrice",
        render: (row: OrderDetailResponse) => (
          <Box sx={{ textAlign: "right" }}>
            {row.unitPrice.toLocaleString("es-AR", {
              minimumFractionDigits: 2,
            })}
          </Box>
        ),
      },
      {
        header: "Total",
        key: "lineTotal",
        render: (row: OrderDetailResponse) => (
          <Box sx={{ textAlign: "right" }}>
            {(row.quantity * row.unitPrice).toLocaleString("es-AR", {
              minimumFractionDigits: 2,
            })}
          </Box>
        ),
      },
    ],
    [],
  );

  const cartTableData = useMemo(
    () => items.map((it) => ({ ...it, id: it.orderDetailId })),
    [items],
  );

  const handleEnviarPedido = async () => {
    if (!orderId?.trim()) return;
    try {
      setIsSending(true);
      await orderService.sendOrder(orderId.trim(), observations);
      showSnackbar("Pedido enviado correctamente", "success", 3000);
      navigate("/orders");
    } catch (error: any) {
      const message: string = error.response?.data?.message ?? "";
      const isStockError =
        message.includes("pedido") && message.includes("disponible");

      if (isStockError) {
        const lines = message
          .split("\n")
          .map((l) => l.trim())
          .filter(Boolean);
        setStockErrors(lines);
      } else {
        showSnackbar(message || "Error al agregar al carrito", "error", 4000);
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleExportExcel = async () => {
    if (!orderId?.trim()) return;
    setIsExporting(true);
    try {
      const blob = await orderService.exportOrder(orderId.trim());
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = `pedido-${orderId}-${Date.now()}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } finally {
      setIsExporting(false);
    }
  };

  if (!orderId) {
    return (
      <Box sx={{ p: 3 }}>
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography variant="h5" fontWeight={700}>
              Carrito
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography color="text.secondary">
              Falta el parámetro <b>orderId</b> en la URL.
            </Typography>
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <Button variant="outlined" onClick={() => navigate("/orders")}>
                Volver
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h4" fontWeight={800}>
            Carrito
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Order ID: {orderNro}
          </Typography>
        </Box>

        <Stack direction="row" spacing={2}>
          <Button variant="outlined" onClick={() => navigate("/orders")}>
            Volver
          </Button>
          {orderStatus == "SENT" && (
            <Button
              variant="contained"
              color="primary"
              startIcon={
                isExporting ? (
                  <CircularProgress size={18} color="inherit" />
                ) : null
              }
              onClick={handleExportExcel}
            >
              {isExporting ? "Descargando..." : "Descargar como excel"}
            </Button>
          )}
          {orderStatus !== "SENT" && (
            <>
              <Button
                variant="contained"
                onClick={() => {
                  navigate(`/orders/create?orderId=${orderId}`);
                }}
              >
                Cargar pedido
              </Button>
              <Button
                variant="contained"
                onClick={handleEnviarPedido}
                disabled={isLoading || isSending || items.length === 0}
              >
                {isSending ? "Enviando…" : "Enviar pedido"}
              </Button>
            </>
          )}
        </Stack>
      </Stack>

      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : items.length === 0 ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <Typography variant="h6" fontWeight={800}>
                Tu carrito está vacío
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 1 }}>
                No encontramos ítems para este pedido.
              </Typography>
              <Stack direction="row" justifyContent="center" sx={{ mt: 3 }}>
                <Button variant="outlined" onClick={() => navigate("/orders")}>
                  Volver
                </Button>
              </Stack>
            </Box>
          ) : (
            <>
              <DataTable
                columns={cartColumns}
                data={cartTableData}
                page={1}
                isLoading={false}
                onChange={() => {}}
                total={items.length}
                rowsPerPage={Math.max(items.length, 1)}
                showPagination={false}
              />

              <TextField
                sx={{ mt: 2 }}
                label="Observaciones"
                placeholder="Notas u observaciones del pedido"
                multiline
                minRows={5}
                fullWidth
                value={observations}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v.length <= 120) {
                    setObservations(v);
                  }
                }}
                disabled={orderStatus === "SENT"}
                inputProps={{ maxLength: 120 }}
                helperText={`${observations.length}/120`}
              />

              <Divider sx={{ my: 2 }} />

              <Stack direction="row" justifyContent="flex-end" gap={2}>
                <Box sx={{ minWidth: 260 }}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography color="text.secondary">Subtotal</Typography>
                    <Typography fontWeight={800}>
                      {subtotal.toLocaleString("es-AR", {
                        minimumFractionDigits: 2,
                      })}
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            </>
          )}
        </CardContent>
      </Card>
      <StockErrorDialog
        open={stockErrors.length > 0}
        errors={stockErrors}
        onClose={() => setStockErrors([])}
      />
    </Box>
  );
};

export default CartPage;
